import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Bitcoin, 
  Plus, 
  ArrowUpRight, 
  RefreshCw,
  Wallet,
  TrendingUp,
  Copy,
  Check,
  ExternalLink,
  ShoppingCart,
  Store,
  Sparkles,
  Info,
} from 'lucide-react';
import { Card, Button, Input, Badge, Modal, Select } from '../components/ui';
import { WalletGenerator } from '../components/crypto';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';
import { formatCurrency, formatDate } from '../lib/utils';

interface CryptoWallet {
  id: string;
  virtualAccountId: string;
  network: string;
  address: string;
  label: string | null;
  createdAt: string;
}

interface Quotation {
  symbol: string;
  priceBrl: number;
  priceUsd?: number;
  change24h?: number;
  provider: string;
  timestamp: string;
  cached: boolean;
}

interface CryptoPurchase {
  id: string;
  network: string;
  cryptoSymbol: string;
  amountBrl: string;
  amountCrypto: string;
  status: string;
  txHash: string | null;
  createdAt: string;
}

interface P2POrder {
  id: string;
  type: string;
  network: string;
  cryptoSymbol: string;
  amountCrypto: string;
  amountBrl: string;
  pricePerUnit: string;
  status: string;
  createdAt: string;
}

interface CryptoSale {
  id: string;
  network: string;
  cryptoSymbol: string;
  amountBrl: string;
  amountCrypto: string;
  status: string;
  createdAt: string;
}

const NETWORKS = [
  { value: 'ETHEREUM', label: 'Ethereum', icon: '⟠' },
  { value: 'BSC', label: 'BNB Smart Chain', icon: '🔶' },
  { value: 'TRON', label: 'TRON', icon: '🔴' },
  { value: 'BITCOIN', label: 'Bitcoin', icon: '₿' },
];

const CRYPTO_SYMBOLS = ['BTC', 'ETH', 'USDT', 'BNB', 'TRX'];

export function Crypto() {
  const queryClient = useQueryClient();
  const [selectedVirtualAccount, setSelectedVirtualAccount] = useState('');
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  // Modals
  const [showRegisterWalletModal, setShowRegisterWalletModal] = useState(false);
  const [showGenerateWalletModal, setShowGenerateWalletModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showP2PModal, setShowP2PModal] = useState(false);

  // Fetch virtual accounts
  const { data: virtualAccountsData } = useQuery({
    queryKey: ['virtual-accounts'],
    queryFn: () => api.get(ENDPOINTS.virtualAccounts.list).then(res => res.data),
  });

  // Extract accounts array from response
  const virtualAccounts = virtualAccountsData?.accounts || virtualAccountsData?.data || (Array.isArray(virtualAccountsData) ? virtualAccountsData : []);

  // Fetch wallets for selected account
  const { data: wallets, isLoading: walletsLoading } = useQuery({
    queryKey: ['crypto-wallets', selectedVirtualAccount],
    queryFn: () => api.get(ENDPOINTS.crypto.wallets(selectedVirtualAccount)).then(res => res.data),
    enabled: !!selectedVirtualAccount,
  });

  // Fetch quotations - always fetch, doesn't depend on virtual account
  const { data: quotationsData, isLoading: quotationsLoading, refetch: refetchQuotations, dataUpdatedAt } = useQuery({
    queryKey: ['crypto-quotations'],
    queryFn: () => api.get(ENDPOINTS.crypto.quotationsPublic, { params: { symbols: 'BTC,ETH,USDT,BNB,SOL,XRP' } }).then(res => res.data),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Extract quotations array from response
  const quotations = quotationsData?.quotations || quotationsData?.data || (Array.isArray(quotationsData) ? quotationsData : []);

  // Format last updated text
  const lastUpdatedText = dataUpdatedAt 
    ? formatDistanceToNow(dataUpdatedAt, { addSuffix: true, locale: ptBR })
    : '';

  // Fetch purchase history for selected account (for display in table)
  const { data: purchasesData } = useQuery({
    queryKey: ['crypto-purchases', selectedVirtualAccount],
    queryFn: () => api.get(ENDPOINTS.crypto.purchases(selectedVirtualAccount)).then(res => res.data),
    enabled: !!selectedVirtualAccount,
  });

  // Extract purchases array from response
  const purchases = purchasesData?.purchases || purchasesData?.data || (Array.isArray(purchasesData) ? purchasesData : []);

  // Fetch ALL purchases from ALL virtual accounts (for holdings calculation)
  const { data: allPurchasesData } = useQuery({
    queryKey: ['crypto-all-purchases', virtualAccounts?.map((a: any) => a.id)],
    queryFn: async () => {
      if (!virtualAccounts?.length) return [];
      const results = await Promise.all(
        virtualAccounts.map((account: any) =>
          api.get(ENDPOINTS.crypto.purchases(account.id))
            .then(res => res.data?.purchases || res.data?.data || (Array.isArray(res.data) ? res.data : []))
            .catch(() => [])
        )
      );
      return results.flat();
    },
    enabled: virtualAccounts?.length > 0,
    refetchInterval: 30000,
  });

  // Fetch ALL sales from ALL virtual accounts (for holdings calculation)
  const { data: allSalesData } = useQuery({
    queryKey: ['crypto-all-sales', virtualAccounts?.map((a: any) => a.id)],
    queryFn: async () => {
      if (!virtualAccounts?.length) return [];
      const results = await Promise.all(
        virtualAccounts.map((account: any) =>
          api.get(ENDPOINTS.crypto.sales(account.id))
            .then(res => res.data?.sales || res.data?.data || (Array.isArray(res.data) ? res.data : []))
            .catch(() => [])
        )
      );
      return results.flat();
    },
    enabled: virtualAccounts?.length > 0,
    refetchInterval: 30000,
  });

  // Fetch P2P orders - always fetch, doesn't depend on virtual account
  const { data: p2pOrders } = useQuery({
    queryKey: ['p2p-orders'],
    queryFn: () => api.get(ENDPOINTS.crypto.marketplace).then(res => res.data),
  });

  // Calculate holdings from ALL completed purchases and sales across ALL accounts
  const holdings = useMemo(() => {
    const allPurchases = allPurchasesData || [];
    const allSales = allSalesData || [];
    
    if (!allPurchases.length || !quotations?.length) return [];
    
    // Group purchases by crypto symbol and sum amounts
    const holdingsMap = new Map<string, { symbol: string; amount: number; totalInvested: number }>();
    
    // Add completed purchases
    allPurchases
      .filter((p: CryptoPurchase) => p.status === 'COMPLETED')
      .forEach((p: CryptoPurchase) => {
        const existing = holdingsMap.get(p.cryptoSymbol) || { symbol: p.cryptoSymbol, amount: 0, totalInvested: 0 };
        existing.amount += parseFloat(p.amountCrypto);
        existing.totalInvested += Number(p.amountBrl) / 100; // Convert centavos to BRL
        holdingsMap.set(p.cryptoSymbol, existing);
      });
    
    // Subtract completed sales
    allSales
      .filter((s: CryptoSale) => s.status === 'COMPLETED')
      .forEach((s: CryptoSale) => {
        const existing = holdingsMap.get(s.cryptoSymbol);
        if (existing) {
          existing.amount -= parseFloat(s.amountCrypto || '0');
          // Don't subtract from totalInvested to keep P&L calculation correct
        }
      });
    
    // Calculate current value based on quotations
    return Array.from(holdingsMap.values())
      .map(h => {
        const quotation = quotations.find((q: Quotation) => q.symbol === h.symbol);
        const currentValue = quotation ? h.amount * quotation.priceBrl : 0;
        const change24h = quotation?.change24h || 0;
        const pnl = currentValue - h.totalInvested;
        const pnlPercent = h.totalInvested > 0 ? (pnl / h.totalInvested) * 100 : 0;
        
        return {
          ...h,
          currentValue,
          change24h,
          pnl,
          pnlPercent,
        };
      })
      .filter(h => h.amount > 0.00000001); // Filter out very small balances
  }, [allPurchasesData, allSalesData, quotations]);

  // Calculate total portfolio value
  const totalPortfolioValue = useMemo(() => {
    return holdings.reduce((sum, h) => sum + h.currentValue, 0);
  }, [holdings]);

  const copyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const getNetworkIcon = (network: string) => {
    const net = NETWORKS.find(n => n.value === network);
    return net?.icon || '🔗';
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      COMPLETED: 'success',
      PENDING: 'warning',
      PROCESSING: 'info',
      FAILED: 'error',
      CANCELLED: 'error',
    };
    return <Badge variant={variants[status] || 'warning'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Criptomoedas</h1>
          <p className="text-surface-400 mt-1">Acompanhe cotações e gerencie suas criptomoedas</p>
        </div>
        <Button variant="ghost" onClick={() => refetchQuotations()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar Cotações
        </Button>
      </div>

      {/* Quotations - Always visible */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Cotações em Tempo Real</h3>
          <p className="text-xs text-surface-400">
            {lastUpdatedText && `Atualizado ${lastUpdatedText} • `}Fonte: CoinGecko API
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quotationsLoading ? (
            [1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="p-4 bg-surface-800/50 rounded-lg animate-pulse">
                <div className="h-16 bg-surface-700 rounded" />
              </div>
            ))
          ) : quotations?.map((q: Quotation) => (
            <div key={q.symbol} className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg font-bold text-white">{q.symbol}</span>
                {q.change24h !== undefined && (
                  <span className={`text-xs font-medium ${q.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {q.change24h >= 0 ? '+' : ''}{q.change24h.toFixed(2)}%
                  </span>
                )}
              </div>
              <p className="text-xl font-bold text-primary-400">
                {formatCurrency(q.priceBrl)}
              </p>
              {q.priceUsd && (
                <p className="text-xs text-surface-400 mt-1">
                  ${q.priceUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* My Holdings - Always visible if user has any holdings (no account selection required) */}
      {holdings.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Meus Holdings</h3>
              <p className="text-xs text-surface-400">
                {lastUpdatedText && `Atualizado ${lastUpdatedText}`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-surface-400">Valor Total</p>
              <p className="text-2xl font-bold text-primary-400">{formatCurrency(totalPortfolioValue)}</p>
            </div>
          </div>
          <div className="space-y-3">
            {holdings.map((holding) => (
              <div
                key={holding.symbol}
                className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border border-surface-700"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <span className="text-lg font-bold text-primary-400">
                      {holding.symbol.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{holding.symbol}</p>
                    <p className="text-sm text-surface-400 font-mono">
                      {holding.amount.toFixed(8)} {holding.symbol}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-white">{formatCurrency(holding.currentValue)}</p>
                  <div className="flex items-center justify-end gap-2">
                    <span className={`text-xs font-medium ${holding.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {holding.change24h >= 0 ? '+' : ''}{holding.change24h.toFixed(2)}% (24h)
                    </span>
                    <span className={`text-xs font-medium ${holding.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {holding.pnl >= 0 ? '+' : ''}{formatCurrency(holding.pnl)} ({holding.pnlPercent.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Account Selection for Operations */}
      <Card className="p-4 bg-surface-800/30 border-primary-500/30">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-surface-300">
            <Info className="w-4 h-4 text-primary-400" />
            <span className="text-sm">Para comprar, vender ou gerenciar carteiras, selecione uma conta:</span>
          </div>
          <Select
            value={selectedVirtualAccount}
            onChange={(e) => setSelectedVirtualAccount(e.target.value)}
            className="w-64"
          >
            <option value="">Selecione uma conta virtual</option>
            {virtualAccounts?.map((account: any) => (
              <option key={account.id} value={account.id}>
                {account.user?.name || account.id.slice(0, 8)}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {selectedVirtualAccount ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Wallets */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Carteiras</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setShowGenerateWalletModal(true)}>
                  <Sparkles className="w-4 h-4 mr-1" />
                  Gerar Nova
                </Button>
                <Button size="sm" onClick={() => setShowRegisterWalletModal(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Importar
                </Button>
              </div>
            </div>

            {walletsLoading ? (
              <div className="space-y-3">
                {[1, 2].map(i => (
                  <div key={i} className="h-20 bg-surface-800 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : wallets?.length === 0 ? (
              <div className="text-center py-8 text-surface-400">
                <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhuma carteira cadastrada</p>
                <div className="flex justify-center gap-3 mt-4">
                  <Button 
                    variant="primary" 
                    onClick={() => setShowGenerateWalletModal(true)}
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Gerar Nova Carteira
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => setShowRegisterWalletModal(true)}
                  >
                    Importar Existente
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {wallets?.map((wallet: CryptoWallet) => (
                  <div
                    key={wallet.id}
                    className="p-4 rounded-lg border border-surface-700 bg-surface-800/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getNetworkIcon(wallet.network)}</span>
                        <div>
                          <p className="font-medium text-white">
                            {wallet.label || wallet.network}
                          </p>
                          <p className="text-sm text-surface-400 font-mono">
                            {wallet.address.slice(0, 10)}...{wallet.address.slice(-8)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyAddress(wallet.address)}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                        >
                          {copiedAddress === wallet.address ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-surface-400" />
                          )}
                        </button>
                        <a
                          href={`https://etherscan.io/address/${wallet.address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                        >
                          <ExternalLink className="w-4 h-4 text-surface-400" />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Ações Rápidas</h3>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="secondary"
                className="flex flex-col items-center py-6"
                onClick={() => setShowBuyModal(true)}
              >
                <ShoppingCart className="w-6 h-6 mb-2 text-green-400" />
                <span>Comprar Cripto</span>
              </Button>
              <Button
                variant="secondary"
                className="flex flex-col items-center py-6"
                onClick={() => setShowSellModal(true)}
              >
                <Store className="w-6 h-6 mb-2 text-red-400" />
                <span>Vender Cripto</span>
              </Button>
              <Button
                variant="secondary"
                className="flex flex-col items-center py-6"
                onClick={() => setShowP2PModal(true)}
              >
                <ArrowUpRight className="w-6 h-6 mb-2 text-blue-400" />
                <span>Mercado P2P</span>
              </Button>
              <Button
                variant="secondary"
                className="flex flex-col items-center py-6"
                disabled
              >
                <TrendingUp className="w-6 h-6 mb-2 text-purple-400" />
                <span>Pools DeFi</span>
              </Button>
            </div>
          </Card>

          {/* Purchase History */}
          <Card className="p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-white mb-4">Histórico de Compras</h3>
            {purchases?.length === 0 ? (
              <p className="text-center text-surface-400 py-8">Nenhuma compra realizada</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-surface-400 text-sm border-b border-surface-700">
                      <th className="pb-3">Data</th>
                      <th className="pb-3">Rede</th>
                      <th className="pb-3">Cripto</th>
                      <th className="pb-3 text-right">Valor BRL</th>
                      <th className="pb-3 text-right">Quantidade</th>
                      <th className="pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases?.map((purchase: CryptoPurchase) => (
                      <tr key={purchase.id} className="border-b border-surface-700/50">
                        <td className="py-3 text-surface-300">{formatDate(purchase.createdAt)}</td>
                        <td className="py-3">
                          <span className="mr-2">{getNetworkIcon(purchase.network)}</span>
                          {purchase.network}
                        </td>
                        <td className="py-3 text-white font-medium">{purchase.cryptoSymbol}</td>
                        <td className="py-3 text-right text-white">
                          {formatCurrency(Number(purchase.amountBrl) / 100)}
                        </td>
                        <td className="py-3 text-right text-primary-400 font-mono">
                          {purchase.amountCrypto}
                        </td>
                        <td className="py-3">{getStatusBadge(purchase.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      ) : (
        <Card className="p-12 text-center">
          <Bitcoin className="w-16 h-16 mx-auto mb-4 text-surface-600" />
          <h3 className="text-xl font-semibold text-white mb-2">Selecione uma conta virtual</h3>
          <p className="text-surface-400">
            Escolha uma conta virtual para gerenciar suas criptomoedas
          </p>
        </Card>
      )}

      {/* Register Wallet Modal */}
      <RegisterWalletModal
        isOpen={showRegisterWalletModal}
        onClose={() => setShowRegisterWalletModal(false)}
        virtualAccountId={selectedVirtualAccount}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['crypto-wallets'] });
          setShowRegisterWalletModal(false);
        }}
      />

      {/* Buy Crypto Modal */}
      <BuyCryptoModal
        isOpen={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        virtualAccountId={selectedVirtualAccount}
        wallets={wallets || []}
        quotations={quotations || []}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['crypto-purchases'] });
          setShowBuyModal(false);
        }}
      />

      {/* Sell Crypto Modal */}
      <SellCryptoModal
        isOpen={showSellModal}
        onClose={() => setShowSellModal(false)}
        virtualAccountId={selectedVirtualAccount}
        quotations={quotations || []}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['crypto-purchases'] });
          setShowSellModal(false);
        }}
      />

      {/* P2P Market Modal */}
      <P2PMarketModal
        isOpen={showP2PModal}
        onClose={() => setShowP2PModal(false)}
        orders={p2pOrders?.sales || p2pOrders?.data || (Array.isArray(p2pOrders) ? p2pOrders : [])}
        virtualAccountId={selectedVirtualAccount}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['p2p-orders'] });
          setShowP2PModal(false);
        }}
      />

      {/* Generate Wallet Modal */}
      <WalletGenerator
        isOpen={showGenerateWalletModal}
        onClose={() => setShowGenerateWalletModal(false)}
        virtualAccountId={selectedVirtualAccount}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['crypto-wallets'] });
          setShowGenerateWalletModal(false);
        }}
      />
    </div>
  );
}


// Register Wallet Modal
function RegisterWalletModal({
  isOpen,
  onClose,
  virtualAccountId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  virtualAccountId: string;
  onSuccess: () => void;
}) {
  const [network, setNetwork] = useState('ETHEREUM');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');

  const registerMutation = useMutation({
    mutationFn: (data: { virtualAccountId: string; network: string; address: string; label?: string }) =>
      api.post(ENDPOINTS.crypto.registerWallet(data.virtualAccountId), data),
    onSuccess,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    registerMutation.mutate({ virtualAccountId, network, address, label: label || undefined });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cadastrar Carteira">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Rede"
          value={network}
          onChange={(e) => setNetwork(e.target.value)}
        >
          {NETWORKS.map(net => (
            <option key={net.value} value={net.value}>
              {net.icon} {net.label}
            </option>
          ))}
        </Select>

        <Input
          label="Endereço da Carteira"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="0x..."
          required
        />

        <Input
          label="Apelido (opcional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ex: Minha carteira principal"
        />

        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-sm text-yellow-400">
            ⚠️ Importante: Cadastre apenas carteiras que você controla. 
            A plataforma não armazena chaves privadas.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={registerMutation.isPending}>
            {registerMutation.isPending ? 'Cadastrando...' : 'Cadastrar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Buy Crypto Modal
function BuyCryptoModal({
  isOpen,
  onClose,
  virtualAccountId,
  wallets,
  quotations,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  virtualAccountId: string;
  wallets: CryptoWallet[];
  quotations: Quotation[];
  onSuccess: () => void;
}) {
  const [walletId, setWalletId] = useState('');
  const [cryptoSymbol, setCryptoSymbol] = useState('BTC');
  const [amountBrl, setAmountBrl] = useState('');

  const selectedQuotation = quotations.find(q => q.symbol === cryptoSymbol);
  const estimatedCrypto = selectedQuotation && amountBrl 
    ? (parseFloat(amountBrl) / selectedQuotation.priceBrl).toFixed(8)
    : '0';

  const purchaseMutation = useMutation({
    mutationFn: (data: { virtualAccountId: string; walletId: string; cryptoSymbol: string; amountBrl: number }) =>
      api.post(ENDPOINTS.crypto.purchase(data.virtualAccountId), data),
    onSuccess,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    purchaseMutation.mutate({
      virtualAccountId,
      walletId,
      cryptoSymbol,
      amountBrl: Math.round(parseFloat(amountBrl) * 100),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Comprar Criptomoeda">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Carteira de Destino"
          value={walletId}
          onChange={(e) => setWalletId(e.target.value)}
          required
        >
          <option value="">Selecione uma carteira</option>
          {wallets.map(wallet => (
            <option key={wallet.id} value={wallet.id}>
              {wallet.label || wallet.network} - {wallet.address.slice(0, 10)}...
            </option>
          ))}
        </Select>

        <Select
          label="Criptomoeda"
          value={cryptoSymbol}
          onChange={(e) => setCryptoSymbol(e.target.value)}
        >
          {CRYPTO_SYMBOLS.map(symbol => (
            <option key={symbol} value={symbol}>{symbol}</option>
          ))}
        </Select>

        <Input
          label="Valor em R$"
          type="number"
          step="0.01"
          min="10"
          value={amountBrl}
          onChange={(e) => setAmountBrl(e.target.value)}
          required
        />

        {selectedQuotation && amountBrl && (
          <div className="p-4 bg-surface-800/50 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-surface-400">Cotação atual:</span>
              <span className="text-white">{formatCurrency(selectedQuotation.priceBrl)}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-surface-400">Você receberá aprox.:</span>
              <span className="text-primary-400 font-mono">{estimatedCrypto} {cryptoSymbol}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={purchaseMutation.isPending || !walletId}>
            {purchaseMutation.isPending ? 'Processando...' : 'Comprar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}


// Sell Crypto Modal
function SellCryptoModal({
  isOpen,
  onClose,
  virtualAccountId,
  quotations,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  virtualAccountId: string;
  quotations: Quotation[];
  onSuccess: () => void;
}) {
  const [cryptoSymbol, setCryptoSymbol] = useState('BTC');
  const [amountCrypto, setAmountCrypto] = useState('');
  const [network, setNetwork] = useState('ETHEREUM');

  const selectedQuotation = quotations.find(q => q.symbol === cryptoSymbol);
  const estimatedBrl = selectedQuotation && amountCrypto 
    ? (parseFloat(amountCrypto) * selectedQuotation.priceBrl * 0.98).toFixed(2) // 2% spread
    : '0';

  const sellMutation = useMutation({
    mutationFn: (data: { virtualAccountId: string; cryptoSymbol: string; amountCrypto: string; network: string }) =>
      api.post(ENDPOINTS.crypto.createSale(data.virtualAccountId), data),
    onSuccess,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sellMutation.mutate({ virtualAccountId, cryptoSymbol, amountCrypto, network });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Vender para Plataforma">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Criptomoeda"
          value={cryptoSymbol}
          onChange={(e) => setCryptoSymbol(e.target.value)}
        >
          {CRYPTO_SYMBOLS.map(symbol => (
            <option key={symbol} value={symbol}>{symbol}</option>
          ))}
        </Select>

        <Select
          label="Rede"
          value={network}
          onChange={(e) => setNetwork(e.target.value)}
        >
          {NETWORKS.map(net => (
            <option key={net.value} value={net.value}>
              {net.icon} {net.label}
            </option>
          ))}
        </Select>

        <Input
          label={`Quantidade de ${cryptoSymbol}`}
          type="number"
          step="0.00000001"
          min="0.00000001"
          value={amountCrypto}
          onChange={(e) => setAmountCrypto(e.target.value)}
          required
        />

        {selectedQuotation && amountCrypto && (
          <div className="p-4 bg-surface-800/50 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-surface-400">Cotação atual:</span>
              <span className="text-white">{formatCurrency(selectedQuotation.priceBrl)}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-surface-400">Spread (2%):</span>
              <span className="text-yellow-400">Aplicado</span>
            </div>
            <div className="flex justify-between text-sm mt-2 pt-2 border-t border-surface-700">
              <span className="text-surface-400">Você receberá aprox.:</span>
              <span className="text-green-400 font-bold">{formatCurrency(parseFloat(estimatedBrl))}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={sellMutation.isPending}>
            {sellMutation.isPending ? 'Processando...' : 'Vender'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// P2P Market Modal
function P2PMarketModal({
  isOpen,
  onClose,
  orders,
  virtualAccountId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  orders: P2POrder[];
  virtualAccountId: string;
  onSuccess: () => void;
}) {
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [cryptoSymbol, setCryptoSymbol] = useState('BTC');
  const [amountCrypto, setAmountCrypto] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [network, setNetwork] = useState('ETHEREUM');

  const createOrderMutation = useMutation({
    mutationFn: (data: any) => api.post(ENDPOINTS.crypto.createSale(virtualAccountId), { ...data, type: 'P2P' }),
    onSuccess: () => {
      setShowCreateOrder(false);
      onSuccess();
    },
  });

  const buyOrderMutation = useMutation({
    mutationFn: (orderId: string) => api.post(ENDPOINTS.crypto.acceptSale(virtualAccountId), { saleId: orderId, buyerAccountId: virtualAccountId }),
    onSuccess,
  });

  const handleCreateOrder = (e: React.FormEvent) => {
    e.preventDefault();
    createOrderMutation.mutate({
      sellerAccountId: virtualAccountId,
      cryptoSymbol,
      amountCrypto,
      pricePerUnit: parseFloat(pricePerUnit),
      network,
    });
  };

  const activeOrders = orders.filter((o: P2POrder) => o.status === 'PENDING');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mercado P2P" size="lg">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-surface-400">Ordens de venda disponíveis</p>
          <Button size="sm" onClick={() => setShowCreateOrder(!showCreateOrder)}>
            {showCreateOrder ? 'Ver Ordens' : 'Criar Ordem'}
          </Button>
        </div>

        {showCreateOrder ? (
          <form onSubmit={handleCreateOrder} className="space-y-4 p-4 bg-surface-800/50 rounded-lg">
            <h4 className="font-medium text-white">Criar Ordem de Venda</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Cripto"
                value={cryptoSymbol}
                onChange={(e) => setCryptoSymbol(e.target.value)}
              >
                {CRYPTO_SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
              <Select
                label="Rede"
                value={network}
                onChange={(e) => setNetwork(e.target.value)}
              >
                {NETWORKS.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Quantidade"
                type="number"
                step="0.00000001"
                value={amountCrypto}
                onChange={(e) => setAmountCrypto(e.target.value)}
                required
              />
              <Input
                label="Preço por unidade (R$)"
                type="number"
                step="0.01"
                value={pricePerUnit}
                onChange={(e) => setPricePerUnit(e.target.value)}
                required
              />
            </div>

            <Button type="submit" disabled={createOrderMutation.isPending} className="w-full">
              {createOrderMutation.isPending ? 'Criando...' : 'Criar Ordem'}
            </Button>
          </form>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {activeOrders.length === 0 ? (
              <p className="text-center text-surface-400 py-8">Nenhuma ordem disponível</p>
            ) : (
              <div className="space-y-3">
                {activeOrders.map((order: P2POrder) => (
                  <div key={order.id} className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-white">
                          {order.amountCrypto} {order.cryptoSymbol}
                        </p>
                        <p className="text-sm text-surface-400">
                          {formatCurrency(parseFloat(order.pricePerUnit))} por unidade
                        </p>
                        <p className="text-sm text-surface-400">
                          Total: {formatCurrency(Number(order.amountBrl) / 100)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => buyOrderMutation.mutate(order.id)}
                        disabled={buyOrderMutation.isPending}
                      >
                        Comprar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4 border-t border-surface-700">
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </Modal>
  );
}

export default Crypto;
